// ─── Create Chase — SME domain constants, types & helpers ─────────────────────
// Extracted from create-chase.tsx. Mirrors server utils/sme/domain.

// Field names, types and enum wording mirror server models/chase-model.ts.
export type IBrandType = "BUSINESS_COMPARE_DIRECT" | "MIND_YOUR_BUSINESS";
export type IPolicyType = "Standard" | "Non-Standard";
export type IPaymentType = "cash" | "instalments";
export type ICoverType = (typeof COVER_TYPES)[number];
/** Which back-office system the policy came from — drives the gateway branch. */
export type IPolicySource = "OGI" | "Acturis";

export const BRAND_OPTIONS: { label: string; value: IBrandType }[] = [
  { label: "BusinessCompare Direct", value: "BUSINESS_COMPARE_DIRECT" },
  { label: "MindYourBusiness", value: "MIND_YOUR_BUSINESS" },
];

export const POLICY_SOURCE_OPTIONS: { label: string; value: IPolicySource }[] = [
  { label: "OGI", value: "OGI" },
  { label: "Acturis", value: "Acturis" },
];

// Cover-type values are the labels themselves — stored as-is on the chase.
export const COVER_TYPES = [
  "Public Liability",
  "Excess of Loss",
  "Goods in Transit",
  "Legal Expenses",
  "Professional Indemnity",
  "Property Owners",
  "Shops and Salons",
  "Offices and Surgeries",
  "Commercial Combined",
  "Pubs and Restaurants",
  "Tools",
] as const;

export const COVER_TYPE_OPTIONS = COVER_TYPES.map((c) => ({ label: c, value: c }));

export const LAPSE_REASONS = [
  "Customer asked not to renew",
  "Too expensive",
  "Found cheaper elsewhere",
  "Shopping around",
  "Business closed / closing",
  "Sold the business / asset",
  "Cover no longer needed",
  "Switching broker",
  "Payment / Direct Debit failure",
  "Poor claims experience",
  "Not given",
].map((r) => ({ label: r, value: r }));

// ─── Chase timeline ───────────────────────────────────────────────────────────
// Mirrors the server: chase-extensions/chase-create-timeline.ts writes 21 rows
// from the day the chase is created, and daily-job.ts fires the touchpoint for
// whatever "days before renewal" that row lands on. So a chase only ever covers
// the window [daysToRenewal - 20, daysToRenewal].

/** Every chase runs a fixed 21-day timeline from the day it is created. */
export const TIMELINE_DAYS = 21;

/** Days before renewal that carry an email (+ SMS on the chased types). */
export const TOUCHPOINT_DAYS = [21, 14, 10, 7, 3, 2, 1];

const list = (days: number[]) =>
  days.length > 1
    ? `${days.slice(0, -1).join(", ")} and ${days[days.length - 1]}`
    : String(days[0] ?? "");

/** Whole days from today until the renewal — UTC midnights, so no DST wobble. */
const daysToRenewal = (renewalDate: string): number => {
  const renewal = new Date(`${renewalDate}T00:00:00Z`).getTime();
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((renewal - today) / 86_400_000);
};

/** The touchpoints a chase created today would actually reach. */
export const coveredTouchpoints = (renewalDate: string): number[] => {
  const days = daysToRenewal(renewalDate);
  return TOUCHPOINT_DAYS.filter((d) => d <= days && d > days - TIMELINE_DAYS);
};

/** Helper line under Renewal Date — what this chase will actually send. */
export const timelineHint = (renewalDate: string): string => {
  if (!renewalDate)
    return `A ${TIMELINE_DAYS}-day timeline starts the moment the chase is created — messages go out ${list(TOUCHPOINT_DAYS)} days before renewal.`;

  const days = daysToRenewal(renewalDate);
  if (Number.isNaN(days)) return "";
  if (days <= 0)
    return "That date isn't in the future — this chase would send nothing.";

  const covered = coveredTouchpoints(renewalDate);
  if (!covered.length)
    return `Renewal is ${days} days away — the ${TIMELINE_DAYS}-day timeline runs out before the first touchpoint, so nothing would send. Create the chase nearer the renewal.`;
  if (covered.length === TOUCHPOINT_DAYS.length)
    return `Full ${TIMELINE_DAYS}-day cadence — messages ${list(TOUCHPOINT_DAYS)} days before renewal.`;

  const unit = covered.length === 1 && covered[0] === 1 ? "day" : "days";
  return `Renewal is ${days} day${days === 1 ? "" : "s"} away — ${covered.length} of ${TOUCHPOINT_DAYS.length} touchpoints left: ${list(covered)} ${unit} before renewal.`;
};

// ─── Invite pricing ───────────────────────────────────────────────────────────
export const QUOTE_SLOTS = ["MAIN", "ALT_1", "ALT_2", "ALT_3", "ALT_4"] as const;
export type QuoteSlot = (typeof QUOTE_SLOTS)[number];

export const QUOTE_SLOT_LABEL: Record<QuoteSlot, string> = {
  MAIN: "Main Invite",
  ALT_1: "Alternative 1",
  ALT_2: "Alternative 2",
  ALT_3: "Alternative 3",
  ALT_4: "Alternative 4",
};

export const QUOTE_LINE_FIELDS = [
  { key: "insurerPremium", label: "Insurer Premium" },
  { key: "brokerFee", label: "Broker Fee" },
  { key: "insurerFee", label: "Insurer Fee" },
  { key: "discount", label: "Discount" },
] as const;
export type QuoteFieldKey = (typeof QUOTE_LINE_FIELDS)[number]["key"];

/** One invite column — insurer, money lines and whether it's the picked quote. */
export type Quote = Record<QuoteFieldKey, number | null> & {
  slot: QuoteSlot;
  insurer: string;
  /** Total payable — premium + IPT + fees − discount. Kept in sync by `withTotal`. */
  premium: number;
  isActive: boolean;
};

export const IPT_RATE = 0.12;

export const r2 = (n: number) => Math.round(n * 100) / 100;

export function toNum(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Number inputs: "" clears the value, anything else is stored as a number. */
export const numOrNull = (value: string): number | null =>
  value.trim() === "" ? null : toNum(value);

export function money(value: number): string {
  return `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Per-quote total: premium + IPT (on premium only) + fees − discount, ≥ 0. */
export function quoteTotal(q: Quote): number {
  const n = (v: number | null) => v ?? 0;
  return Math.max(
    0,
    r2(
      n(q.insurerPremium) * (1 + IPT_RATE) +
        n(q.insurerFee) +
        n(q.brokerFee) -
        n(q.discount),
    ),
  );
}

/** Recompute the stored total after any line-item edit. */
export const withTotal = (q: Quote): Quote => ({ ...q, premium: quoteTotal(q) });

export const hasQuoteValue = (q: Quote) =>
  q.insurer.trim() !== "" || QUOTE_LINE_FIELDS.some((f) => q[f.key] !== null);

/** A column the user hasn't touched — rendered, but never kept in state. */
export const blankQuote = (slot: QuoteSlot): Quote => ({
  slot,
  insurer: "",
  insurerPremium: null,
  brokerFee: null,
  insurerFee: null,
  discount: null,
  premium: 0,
  isActive: false,
});

/** Keep stored quotes in slot order (MAIN first). */
export const orderQuotes = (quotes: Quote[]): Quote[] =>
  QUOTE_SLOTS.flatMap((s) => quotes.filter((q) => q.slot === s));

// ─── Renewal documents ────────────────────────────────────────────────────────
export type DocKind =
  | "TOBA"
  | "STATEMENT_OF_FACT"
  | "NOTICE_TO_POLICY_HOLDER"
  | "SCHEDULE"
  | "OTHER";

/** An attached document — mirrors chase-renewal-documents. `key` is the UI row. */
export type ChaseDocument = {
  key: string;
  name: string;
  kind: DocKind;
  fileName: string;
  /** The picked file as a base64 data URL — what the server puts on S3. */
  file: string | null;
  fee: number | null;
};

/** Files ride in the same JSON body as the chase, so they go up as base64. */
export const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/** The always-rendered upload slots. `key` doubles as the row id. */
export const NAMED_DOCS: { key: string; kind: DocKind; name: string; showFee: boolean }[] =
  [
    { key: "TOBA", kind: "TOBA", name: "TOBA", showFee: true },
    {
      key: "STATEMENT_OF_FACT",
      kind: "STATEMENT_OF_FACT",
      name: "Statement of Fact",
      showFee: false,
    },
    {
      key: "NOTICE_TO_POLICY_HOLDER",
      kind: "NOTICE_TO_POLICY_HOLDER",
      name: "Notice to Policy Holder",
      showFee: false,
    },
    { key: "SCHEDULE", kind: "SCHEDULE", name: "Schedule", showFee: false },
  ];

// ─── Single form-state shape ──────────────────────────────────────────────────
// The whole page runs off ONE state object; this is (close to) the request body
// so createChase can send it as `...value`. Agent is intentionally absent — it's
// auto-assigned in the background later.
export type CreateChaseForm = {
  forename: string;
  surname: string;
  companyName: string;
  email: string;
  telephone: string;
  dateOfBirth: string;
  postcode: string;
  address: string;
  policyReferenceNumber: string;
  /** What tells two chases on the same policy reference apart. */
  versionReferenceNumber: string;
  brandCode: IBrandType;
  policySource: IPolicySource;
  coverType: ICoverType;
  policyNumber: string;
  insurer: string;
  renewalDate: string;
  policyType: IPolicyType;
  paymentType: IPaymentType;
  auto: boolean;
  lapseReason: string;
  instalments: number;
  interestRate: number;
  /** Only the columns the agent actually filled in. */
  quotes: Quote[];
  /** Only the documents actually attached. */
  documents: ChaseDocument[];
};

export const initialForm = (): CreateChaseForm => ({
  forename: "",
  surname: "",
  companyName: "",
  email: "",
  telephone: "",
  dateOfBirth: "",
  postcode: "",
  address: "",
  policyReferenceNumber: "",
  versionReferenceNumber: "",
  brandCode: BRAND_OPTIONS[0].value,
  policySource: POLICY_SOURCE_OPTIONS[0].value,
  coverType: COVER_TYPES[0],
  policyNumber: "",
  insurer: "",
  renewalDate: "",
  policyType: "Standard",
  paymentType: "cash",
  auto: false,
  lapseReason: "",
  instalments: 10,
  interestRate: 20,
  quotes: [],
  documents: [],
});
